/// <amd-module name="VSSUI/Components/PersonaCard/PersonaCardContent" />
import * as React from "react";
import * as Resources from "VSSUI/VSS.Resources.VSSUI";

import { IPoint } from "OfficeFabric/Utilities";
import { Callout, DirectionalHint } from "OfficeFabric/Callout";
import { FocusZone, FocusZoneDirection, FocusZoneTabbableElements, IFocusZone } from "OfficeFabric/FocusZone";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { DelayedRender } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import { ContactCard } from "./ContactCard";
import { DefaultAbridgedCard } from "./DefaultAbridgedCard";
import { DefaultSimpleCard } from "./DefaultSimpleCard";
import { DefaultCard } from "./DefaultCard";
import { OrganizationCard } from "./OrganizationCard";
import { CardType, PersonaCardContentProps, IIdentity } from "./PersonaCard.Props";
import * as Utils from "./PersonaCardIdentityUtils";
import { PersonaCardHeaderElement as HeaderElement } from "./PersonaCardHeaderElement";

/**
 * The content of the callout for the persona card.
 */
/** @deprecated */
export class PersonaCardContent extends React.Component<PersonaCardContentProps> {
    private _focusZone: IFocusZone;

    public componentDidUpdate(): void {
        const isVsdUser = Utils.isVsdUser(this.props.dataProps.identity);
        /*
         * Avoid set focus getting called multiple times, since the header element will set it after it mounts/updates
         * Also for default abridged card, set it only after focus zone ref is available
         */
        if (!this.props.dataProps.header && !isVsdUser) {
            this._focusOnUpdate();
        }
    }

    // Render
    public render(): JSX.Element {
        // Get target element
        const targetElement: HTMLElement | IPoint = this.props.referenceHTMLComponent ? this.props.referenceHTMLComponent : this.props.target;
        // Use component state information to render component
        return (
            <div>
                <Callout
                    onDismiss={this._onDismissCallout}
                    target={targetElement}
                    ariaLabel={Resources.PeoplePickerProfileCardAriaLabel}
                    role="alert"
                    isBeakVisible={false}
                    gapSpace={10}
                    className="persona-card-callout"
                    directionalHint={DirectionalHint.rightTopEdge}
                >
                    <div>{this._renderCard()}</div>
                </Callout>
            </div>
        );
    }

    private _renderCard(): JSX.Element {
        const cssClassName = "bolt-persona-card-styles";
        if (this.props.working && !Utils.isCompleteIdentity(this.props.dataProps.identity)) {
            // Still working to get initial data
            return (
                <div className={css(cssClassName, "loading")}>
                    <DelayedRender delay={300}>
                        <Spinner label={Resources.Loading} size={SpinnerSize.large} className="persona-card-loading-spinner" />
                    </DelayedRender>
                </div>
            );
        }

        const isAdOrAad = Utils.isAdUser(this.props.dataProps.identity) || Utils.isAadUser(this.props.dataProps.identity);
        const headerIdentity: IIdentity = this.props.dataProps.header || null;

        return (
            <FocusZone
                className={cssClassName}
                handleTabKey={FocusZoneTabbableElements.all}
                isCircularNavigation={true}
                componentRef={this._onFocusZoneRef}
                direction={FocusZoneDirection.vertical}
            >
                {isAdOrAad && headerIdentity && (
                    <HeaderElement identity={headerIdentity} onClickFunction={this.props.onHeaderClick} setFocus={this._focusOnUpdate} />
                )}
                {this._renderInnerCard()}
            </FocusZone>
        );
    }

    private _onFocusZoneRef = (focusZone: IFocusZone): void => {
        this._focusZone = focusZone;
        this._focusOnUpdate();
    };

    private _focusOnUpdate = (): void => {
        const directReport = this.props.dataProps.directReportList;
        // Setting the focus only if card is loaded completely for which case directReportList is not null
        if (this.props.dataProps.cardType === CardType.Default && directReport) {
            this._focusZone.focus();
        } else if (this.props.dataProps.cardType !== CardType.Default) {
            this._focusZone.focus();
        }
    };

    private _renderInnerCard(): JSX.Element {
        // Get component state data
        const { identity, managerList, directReportList } = this.props.dataProps;

        if (!identity) {
            return null;
        }

        const isVsdUser = Utils.isVsdUser(identity);
        const isWmdUser = Utils.isWmdUser(identity);
        const isAadUser = Utils.isAadUser(identity);
        const isAdUser = Utils.isAdUser(identity);

        if (isVsdUser) {
            return <DefaultAbridgedCard identity={identity} />;
        } else if (isWmdUser) {
            return <DefaultSimpleCard identity={identity} />;
        } else if (isAadUser || isAdUser) {
            switch (this.props.dataProps.cardType) {
                case CardType.Contact:
                    return <ContactCard identity={identity} />;
                case CardType.Organization:
                    return (
                        <OrganizationCard
                            identity={identity}
                            managerList={managerList}
                            directReportList={directReportList}
                            onClickEntity={this.props.onClickEntity}
                        />
                    );
                case CardType.Default:
                default:
                    const manager = managerList && managerList.length > 0 ? managerList[managerList.length - 1] : null;
                    return (
                        <DefaultCard
                            identity={identity}
                            manager={manager}
                            isPreviousHeader={!!this.props.dataProps.previousDataState || !!this.props.initialHeader}
                            showContactCard={this.props.onShowContactCard}
                            showOrganizationCard={this.props.onShowOrganizationCard}
                            onClickEntity={this.props.onClickEntity}
                            setFocus={this._focusOnUpdate}
                        />
                    );
            }
        }

        return null;
    }

    // State change helper methods
    private _onDismissCallout = (): void => {
        if (this.props.onDismissCallback) {
            this.props.onDismissCallback();
        }
    };
}
